import React, { useEffect, useState } from 'react';
import { useCurrentStep } from '../../hooks/useCurrentStep';
import { useGetCountries } from '../../hooks/useGetCountries';
import { useViewSize } from '../../hooks/useViewSize';
import { AppContextType } from './appContextTypes';

const AppContext = React.createContext<
AppContextType | undefined
>(undefined);

type Props = {
  children?: any;
};

// Make sure to use AppProvider one level up from where you want to consume values
const AppProvider = ({ children }: Props) => {
  const [history, setHistoryState] = useState(null);
  const [flags, setFlags] = useState({});
  const { currentStep, goToStep, goToCreateAccount } = useCurrentStep(history);
  const viewSize = useViewSize();
  const countries = useGetCountries();
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  function setHistory(updatedHistory: any): void {
    if (!updatedHistory) {
      // eslint-disable-next-line no-console
      console.error('History was not set from useHistory in SocialEcommRegistrationRoutes');
    }
    useEffect(() => {
      setHistoryState(updatedHistory);
    }, [history]);
  }

  // check if recurly has been loaded
  const [recurlyLoaded, setRecurlyLoaded] = useState(false);
  function updateRecurlyLoaded() {
    let attempts = 0;
    const maxAttempts = 200; // wait 10 seconds and then give up
    const recurlyLoadedInterval = setInterval(() => {
      attempts += 1;
      if (window.recurly || attempts === maxAttempts) {
        setRecurlyLoaded(true);
        clearInterval(recurlyLoadedInterval);
      }
    }, 50);
    return null;
  }

  useEffect(() => {
    updateRecurlyLoaded();
  }, []);

  return (
    <AppContext.Provider value={{
      viewSize,
      currentStep,
      goToStep,
      goToCreateAccount,
      setHistory,
      countries,
      flags,
      setFlags,
      showPaymentForm,
      setShowPaymentForm,
      recurlyLoaded,
    }}
    >
      {children}
    </AppContext.Provider>
  );
};

// This hook is the equivalent of a context consumer
// TO USE: const { viewSize } = useUserContext();
// MAKE SURE: <AppProvider> is a level up
export function useAppContext() {
  const context = React.useContext(AppContext);
  if (!context) {
    throw new Error(
      'You must use the App Provider in order to consume this context.',
    );
  }
  return context;
}

export default AppProvider;

AppProvider.defaultProps = {
  children: {},
};
